import React, { useMemo } from "react";
import "../home-styling/Help.css";

// Constants for better maintainability
const FAQ_DATA = [
  {
    question: "How do I get my account verified?",
    answer: "To get your account verified, please ensure your profile information is complete and accurate. Our team will review your application and contact you if additional information is needed. Verification typically takes 1-3 business days."
  },
  {
    question: "How do I create a tour package?",
    answer: "Navigate to the Dashboard and click on the 'Create Package' button. Fill in all the required details including package name, description, pricing, itinerary, and upload attractive images. Once submitted, your package will be available for customers to book."
  },
  {
    question: "How do I manage bookings?",
    answer: "Go to the Bookings section to view all customer bookings. You can confirm, cancel, or view details of each booking. Make sure to respond to booking requests within 24 hours."
  },
  {
    question: "When will I receive payments?",
    answer: "Payments are processed automatically while booking is confirmed by the customers."
  },
  {
    question: "How do I update my bank account details?",
    answer: "Go to your Profile section and navigate to the Bank Account Information card. You can add, edit, or update your bank details there. Make sure the information is accurate to avoid payment delays."
  },
  {
    question: "What should I do if I encounter technical issues?",
    answer: "If you're experiencing technical issues, please try refreshing the page first. If the problem persists, contact our support team at uracabtech@gmail.com or call our helpline at +91- 8974607362."
  }
];

const CONTACT_INFO = [
  {
    method: "Email Support",
    details: "uracabtech@gmail.com",
    description: "For general inquiries and technical support"
  },
  {
    method: "Phone Support",
    details: "+91-89746-07362",
    description: "Available Monday to Saturday, 10 AM to 5 PM"
  },
  {
    method: "Emergency Contact",
    details: "+91-93666 23719",
    description: "For urgent booking-related issues during tours"
  }
];

const QUICK_LINKS = [
  {
    title: "Package Creation Guide",
    description: "Learn how to create attractive tour packages",
    link: "#"
  },
  {
    title: "Booking Management",
    description: "Understand how to manage customer bookings",
    link: "#"
  },
  {
    title: "Profile Verification",
    description: "Steps to get your account verified",
    link: "https://www.youtube.com/watch?v=x-G-8ZTk33g"
  }
];

const STATS_DATA = [
  {
    title: "Support",
    description: "Round-the-clock assistance for urgent issues"
  },
  {
    title: "Quick Response",
    description: "Typically within 2 hours during business hours"
  },
  {
    title: "Comprehensive Guides",
    description: "Step-by-step tutorials and documentation"
  }
];

const Help = ({ loading }) => {
  // Memoized components
  const FaqItem = useMemo(() => ({ question, answer }) => (
    <div className="faq-item">
      <h3>{question}</h3>
      <p>{answer}</p>
    </div>
  ), []);

  const StatCard = useMemo(() => ({ title, description }) => (
    <div className="stat-card">
      <div className="stat-content">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  ), []);

  const QuickLinkCard = useMemo(() => ({ title, description, link }) => (
    <a 
      href={link} 
      className="quick-link-card"
      target={link.startsWith('http') ? '_blank' : '_self'}
      rel={link.startsWith('http') ? 'noopener noreferrer' : undefined}
    >
      <h3>{title}</h3>
      <p>{description}</p>
      <span className="link-arrow">→</span>
    </a>
  ), []);

  const ContactCard = useMemo(() => ({ method, details, description }) => (
    <div className="contact-card">
      <div className="contact-details">
        <h3>{method}</h3>
        <p className="contact-info">{details}</p>
        <p className="contact-description">{description}</p>
      </div>
    </div>
  ), []);

  // Memoized render sections
  const renderStats = useMemo(() => (
    <div className="help-stats">
      {STATS_DATA.map((stat, index) => (
        <StatCard
          key={index}
          title={stat.title}
          description={stat.description}
        />
      ))}
    </div>
  ), [StatCard]);

  const renderFaqSection = useMemo(() => (
    <div className="faq-section">
      <h2>Frequently Asked Questions</h2>
      <div className="faq-list">
        {FAQ_DATA.map((faq, index) => (
          <FaqItem
            key={index}
            question={faq.question}
            answer={faq.answer}
          />
        ))}
      </div>
    </div>
  ), [FaqItem]);

  const renderQuickLinks = useMemo(() => (
    <div className="quick-links-section">
      <h2>Quick Links & Resources</h2>
      <div className="quick-links-grid">
        {QUICK_LINKS.map((link, index) => (
          <QuickLinkCard
            key={index}
            title={link.title}
            description={link.description}
            link={link.link}
          />
        ))}
      </div>
    </div>
  ), [QuickLinkCard]);

  const renderContactSection = useMemo(() => (
    <div className="contact-section">
      <h2>Contact Support</h2>
      <div className="contact-methods">
        {CONTACT_INFO.map((contact, index) => (
          <ContactCard
            key={index}
            method={contact.method}
            details={contact.details}
            description={contact.description}
          />
        ))}
      </div>
    </div>
  ), [ContactCard]);

  const renderLoadingState = useMemo(() => (
    <div className="help-section">
      <div className="loading">Loading help content...</div>
    </div>
  ), []);

  const renderHelpContent = useMemo(() => (
    <div className="help-section">
      <div className="help-header">
        <h1>Help & Support</h1>
        <p>Find answers to common questions and get support for your operator account</p>
      </div>

      {renderStats}
      {renderFaqSection}
      {renderQuickLinks}
      {renderContactSection}
    </div>
  ), [renderStats, renderFaqSection, renderQuickLinks, renderContactSection]);

  // Main render
  if (loading.help) {
    return renderLoadingState;
  }

  return renderHelpContent;
};

export default React.memo(Help);